<template>
  <div class="dashboard">
    <div class="main-logo">
      Neighbor Dashboard
    </div>
    <div style="display: table; width: 100%; vertical-align: center;margin-top: 2%">
      <div
          style="display: table-cell; vertical-align: center; text-align: center;padding: 0 25%;"
          class="search-form">
        <div @keyup.enter="load">
          <el-input
              v-model.trim="searchCriteria"
              style="display: inline-block; vertical-align: middle; text-align: center;"
              placeholder="Search by e-mail, Mac Address, Serial Number, Order ID or PLC uuid/gid"
              size="small"
              @input="searchPatternError = false">
            <i
                slot="prefix"
                class="el-input__icon el-icon-search"
                style="line-height: 25px;" />
          </el-input>
        </div>
        <el-col
            :span="24"
            class="tip-message">
          <span
              v-if="searchType === 'Unknown'"
              :class="{'warning': searchPatternError}">Search by e-mail, Mac Address, Serial Number, Order ID or PLC uuid/gid</span>
        </el-col>
        <el-button
            v-loading="loading"
            style="display: inline-block; vertical-align: middle; text-align: center;"
            type="primary"
            @click="load">
          <span>Search</span>
        </el-button>
      </div>
    </div>

    <DeviceSelector
        v-if="openDeviceSelectorModal"
        :devices="deviceMatches"
        :profile="deviceProfile"
        :open.sync="openDeviceSelectorModal" />
    <ProfileCreator
        v-if="openProfileCreatorModal"
        :device-id="searchCriteria"
        :open.sync="openProfileCreatorModal" />
    <RingSolutionsRedirectDialog
        v-if="result"
        :device="result"
        :open.sync="openRedirectToRingSolutions" />
    <OrderDetails
        v-if="detailsVisible"
        :visible.sync="detailsVisible"
        :order-number="order_id" />
  </div>
</template>

<script>
import { get, values, isEmpty } from "lodash";
import { EventBus } from "../eventBus";
import searchMixin from "./../mixins/search";
import { createNamespacedHelpers as module } from "vuex";
const { mapActions: deviceActions } = module("db/device");
const { mapActions: uiActions } = module("ui");
const { mapActions: accountActions } = module("db/account");
const { mapActions: requestActions } = module("db/requests");

import RingSolutionsRedirectDialog from "@/containers/Core/RingSolutionsRedirectDialog";
import ProfileCreator from "@/components/Shared/ProfileCreator";
import DeviceSelector from "@/components/Shared/DeviceSelector";

import axios from "axios";
import OrderDetails from "./Customer/OrderWidget/OrderDetails.vue";

const {
  mapGetters: sharedGetters,
  mapMutations: sharedMutations,
  mapActions: sharedActions,
} = module("shared");

export default {
  name: "HomePage",
  components: {
    OrderDetails,
    RingSolutionsRedirectDialog,
    ProfileCreator,
    DeviceSelector,
  },
  mixins: [searchMixin],
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const profile = vm.readMessage("device/profile");
      if (profile) {
        EventBus.$emit("device-profile-only", profile);
      }
    });
  },
  data() {
    return {
      deviceMatches: [],
      deviceProfile: {},
      notFound: false,
      openDeviceSelectorModal: false,
      openRedirectToRingSolutions: false,
      openProfileCreatorModal: false,
      searchCriteria: "",
      loading: false,
      criteria: {},
      searchPatternError: false,
      result: null,
      detailsVisible: false,
      order_id: "",
    };
  },
  computed: {
    ...sharedGetters([
      "readMessage",
      "reasons",
      "products",
      "userHasPermission",
    ]),
    searchType() {
      return this.getSearchEntity(this.searchCriteria);
    },
  },
  created() {
    this.fetchMasterData();
  },
  methods: {
    ...sharedActions(["fetchMasterData"]),
    ...deviceActions(["fetchDeviceDetailed"]),
    ...uiActions(["setLoading"]),
    ...sharedMutations(["sendMessage"]),
    ...accountActions(["globalSearch"]),
    ...requestActions(["fetchRequestsBy"]),
    loadMasterData() {
      if (isEmpty(this.reasons) || isEmpty(this.products))
        this.fetchMasterData();
    },
    load() {
      this.searchPatternError = false;
      if (this.searchCriteria.trim()) {
        this.criteria = {
          model: this.searchType,
          criteria: this.searchCriteria.trim(),
        };
        if (this.searchType === "Customer") {
          this.loadCustomer();
        } else if (["Device", "Alarm", "DSN"].includes(this.searchType)) {
          this.loadDevice();
        } else if (this.searchType.match(/^plc-/)) {
          this.loadPLCRequest();
        } else if (this.searchType === "order") {
          this.loadOrder();
        } else {
          this.searchPatternError = true;
        }
      }
    },
    startLoading() {
      this.loading = true;
      this.setLoading(true);
    },
    stopLoading() {
      this.loading = false;
      this.setLoading(false);
    },
    loadOrder() {
      if (
        this.userHasPermission({
          permission: "OrderGlobalSearch",
          on: "NewUI",
        })
      ) {
        let uri = `/v3/orders/details`;
        axios
          .get(uri, { params: { order_ids: [this.searchCriteria] } })
          .then((response) => {
            this.order_id = this.searchCriteria;
            this.detailsVisible = true;
          })
          .catch((error) => {
            this.$message({
              type: "error",
              message: `Order #${this.searchCriteria} not found`,
            });
          })
          .finally(() => {
            this.stopLoading();
          });
      } else {
        this.$message({
          type: "error",
          message: `You don't have permission to search an order`,
        });
      }
    },
    loadCustomer() {
      this.startLoading();
      const onComplete = () => {};
      const onAbort = () => {
        this.stopLoading();
        this.$notify.warning({
          title: `Customer with Email ${this.searchCriteria}`,
          message: "Not Found",
          duration: 5000,
        });
      };

      this.globalSearch({ criteria: this.searchCriteria, model: "Customer" })
        .then(({ found, id }) => {
          if (found)
            this.$router.push(`/customer/${id}/dashboard`, onComplete, onAbort);
        })
        .catch(onAbort);
    },
    loadDevice() {
      this.startLoading();
      const { criteria: id, _ } = this.criteria;
      const deviceNotFound = (resp) => {
        this.$notify.warning({
          title: `Device ${id}`,
          message: resp.message,
          duration: 5000,
        });
        this.stopLoading();
        if (!resp.meta.is_accessory) this.openProfileCreatorModal = true;
      };
      this.fetchDeviceDetailed({ id, customer_auth: false })
        .then((response) => {
          if (response.meta["is_alarm_device"]) {
            this.openRedirectToRingSolutions = true;
            this.result = response.device;
            this.stopLoading();
            return;
          } else if (response.meta.full_data) {
            let other_devices = response.other_devices || [];
            this.deviceProfile = response.profile;
            if (other_devices.length > 1) {
              this.deviceMatches = other_devices;
              this.openDeviceSelectorModal = true;
            } else {
              let currentDev = other_devices[0] || response.device;
              if (currentDev.deleted_at || currentDev.deactivated_at)
                EventBus.$emit("device-profile-only", {
                  ...response.profile,
                  deleted_at: response.device.deleted_at || undefined,
                  deactivated_at: response.device.deactivated_at || undefined,
                });
              else
                this.$router.push(
                  `/customer/${currentDev.owner_id}/devices/${currentDev.doorbot_id}`,
                  this.stopLoading, // onComplete
                  this.stopLoading // onAbort
                );
            }
          } else if (
            response.device.setups_history.length === 0 ||
            response.meta.profile_only
          ) {
            EventBus.$emit("device-profile-only", response.profile);
          } else {
            deviceNotFound({ message: "Not Found", meta: {} });
          }
          this.stopLoading();
        })
        .catch((error) => {
          if (error.data && error.data.message) deviceNotFound(error.data);
          else deviceNotFound({ message: "Not found", meta: {} });
        });
    },
    loadPLCRequest() {
      let idMap = { "plc-uuid": "uuid", "plc-gid": "gid" };
      let idName = idMap[this.searchType];
      let pathToId = `[0][0].${idName}`;
      this.startLoading();
      const onComplete = () => {};
      const onAbort = () => {
        this.stopLoading();
        this.$notify.warning({
          title: `Customer Request (${this.searchCriteria})`,
          message: "Not Found",
          duration: 5000,
        });
      };
      let query = {};
      let id = this.searchCriteria.trim();
      this.fetchRequestsBy({ id })
        .then((resp) => {
          if (resp) {
            // search by plc uuid/gid is case insensitive, Request page search is not.
            // hence, we get the plc-uuid|gid from the response
            query[this.searchType] = get(values(resp.requests), pathToId, null);
            this.$router.push(
              {
                path: `/customer/${resp.customer_id}/requests`,
                query,
              },
              onComplete,
              onAbort
            );
          }
        })
        .catch(onAbort);
    },
  },
};
</script>

<style lang="scss">
.main-logo {
  font-size: 4em;
  text-align: center;
  color: #606266;
}
.dashboard {
  margin: 30px 0px;
  margin-top: 10%;
  height: 100%;
}
.search-form {
  /*margin: 30px auto;*/
  .el-input .el-input__prefix {
    padding: 5px;
    color: $--color-dusty-gray;
  }
  .el-input--prefix .el-input__inner {
    padding-left: 47px;
  }
  input {
    font-size: 20px;
    padding: 25px;
    border-radius: 55px;
  }
  button {
    margin-top: 10px;
    width: 30%;
    font-weight: 400;
    font-size: $--font-size-large;
  }
  i {
    font-size: 30px;
  }
  .el-input__icon.el-icon-search {
    line-height: 42px;
  }

  .tip-message {
    margin-top: 10px;
    text-align: center;
    color: #999;
    position: relative;
    min-height: 20px;
    span.warning {
      color: $--color-milano-red;
    }
  }
}
</style>
